import moment from 'moment';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';

// Stripe Connect is only offered in Sweden and Norway
export const CONNECT_COUNTRIES = ['SE', 'NO'];
const COUNTRY_BY_CURRENCY = { SEK: 'SE', NOK: 'NO' };

// The club config has no country, so go by its currency
export const countryFromCurrency = (currency) =>
  COUNTRY_BY_CURRENCY[`${currency || ''}`.toUpperCase()] || 'SE';

// Fee limits, as the transactions service checks them
export const FEE_PERCENT_MAX = 30;
export const FEE_FIXED_MAX_KR = 100;

/**
 * Where a receiver is in onboarding: disconnected, onboarding (not
 * finished), needsDetails (Stripe asks for more), reviewing (submitted, Stripe
 * hasn't enabled payments yet), feeNotSet (waiting for us) or ready.
 */
export const receiverStatus = (receiver) => {
  if (receiver.disconnectedAt) {
    return 'disconnected';
  }
  if (!receiver.stripeAccountId || !receiver.detailsSubmitted) {
    return 'onboarding';
  }
  if (receiver.requirementsDue?.length) {
    return 'needsDetails';
  }
  if (!receiver.chargesEnabled || !receiver.payoutsEnabled) {
    return 'reviewing';
  }
  if (receiver.feePercent === null || receiver.feePercent === undefined) {
    return 'feeNotSet';
  }
  return 'ready';
};

export const STATUS_COLORS = {
  disconnected: 'danger',
  onboarding: 'secondary',
  needsDetails: 'warning',
  reviewing: 'info',
  feeNotSet: 'info',
  ready: 'success',
};

// Stripe's requirement codes, by their start, as readable groups. The person
// ones come as e.g. representative.dob.day or person_1Abc.verification.document.
const REQUIREMENT_GROUPS = [
  [/^external_account/, 'bankAccount'],
  [/^tos_acceptance/, 'terms'],
  [/^business_profile\.url/, 'website'],
  [/^business_profile\.(mcc|product_description)/, 'business'],
  [/^business_profile/, 'businessProfile'],
  [/^business_type/, 'businessType'],
  [/^company\.verification/, 'companyDocument'],
  [/^company\.tax_id/, 'companyTaxId'],
  [/^company\.address/, 'companyAddress'],
  [/^company/, 'company'],
  [/\.verification\.additional_document/, 'addressDocument'],
  [/\.verification\.document/, 'identityDocument'],
  [
    /^(individual|representative|person_[^.]+)\.(id_number|ssn_last_4)/,
    'idNumber',
  ],
  [/^(individual|representative|person_[^.]+)\.address/, 'personAddress'],
  [
    /^(individual|representative|person_[^.]+|owners|directors|executives)/,
    'person',
  ],
];

/** The readable groups of what Stripe still needs (unknown codes: "other"). */
export const requirementLabels = (requirementsDue = []) => {
  const p = partial('connect');
  const groups = requirementsDue.map(
    (code) =>
      REQUIREMENT_GROUPS.find(([pattern]) => pattern.test(code))?.[1] || 'other'
  );
  return [...new Set(groups)].map((group) => p(`requirement-${group}`));
};

const formatDecimal = (value) =>
  Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Øre as kronor, e.g. "1,80 kr". */
export const formatKr = (ore) =>
  `${formatDecimal((Number(ore) || 0) / 100)} ${partial('connect')('kr')}`;

/** Øre in a report row's currency, e.g. "1 234,50 SEK". */
export const formatAmount = (ore, currency) =>
  `${formatDecimal((Number(ore) || 0) / 100)} ${currency || ''}`.trim();

/** e.g. "6.5 % + 1,80 kr"; null when no percentage is set. */
export const formatFee = (percent, fixedAmount) =>
  percent === null || percent === undefined
    ? null
    : `${Number(percent).toLocaleString()} % + ${formatKr(fixedAmount)}`;

/**
 * The platform's share of a 100 kr payment, in øre (never more than the
 * payment). Stripe's fee is paid out of it, so the platform keeps less.
 */
export const feeOn100Kr = (percent, fixedAmount) =>
  Math.min(
    10000,
    Math.round((10000 * (Number(percent) || 0)) / 100) +
      (Number(fixedAmount) || 0)
  );

// The kinds of product a receiver can have its own fee for (the payment
// options' entity types)
export const PRODUCT_TYPES = [
  'tier',
  'auction_item',
  'goal_subscription',
  'lottery_ticket',
];

/** e.g. "6.5 % + 1,80 kr · Auctions 10 % + 0,00 kr"; null without a fee. */
export const formatReceiverFees = (receiver) => {
  const p = partial('connect');
  const fees = [
    formatFee(receiver.feePercent, receiver.feeFixedAmount),
    ...PRODUCT_TYPES.filter((type) => receiver.productFees?.[type]).map(
      (type) =>
        `${p(`product-${type}`)} ${formatFee(
          receiver.productFees[type].feePercent,
          receiver.productFees[type].feeFixedAmount
        )}`
    ),
  ].filter(Boolean);
  return fees.length ? fees.join(' · ') : null;
};

// Typed amounts may use a decimal comma
export const parseDecimal = (value) =>
  Number(`${value ?? ''}`.trim().replace(',', '.'));
export const hasMaxTwoDecimals = (value) =>
  /^\d+([.,]\d{1,2})?$/.test(`${value ?? ''}`.trim());
export const krToOre = (value) => Math.round(parseDecimal(value) * 100);
export const oreToKr = (ore) =>
  ore === null || ore === undefined ? '' : `${ore / 100}`;

/** Club names by id, for super-users' lists; empty if they can't be read. */
export const fetchClubNames = async () => {
  try {
    const { data } = await Api.clubadmin.all();
    return Object.fromEntries(
      (data || []).map((club) => [`${club.id}`, club.config?.club_name])
    );
  } catch {
    return {};
  }
};

// The report's quick periods; the report takes [from, to)
export const REPORT_PERIODS = ['thisMonth', 'lastMonth', 'thisYear'];

export const periodRange = (period) => {
  switch (period) {
    case 'lastMonth': {
      const start = moment().subtract(1, 'month').startOf('month');
      return { from: start, to: start.clone().add(1, 'month') };
    }
    case 'thisYear': {
      const start = moment().startOf('year');
      return { from: start, to: start.clone().add(1, 'year') };
    }
    default: {
      const start = moment().startOf('month');
      return { from: start, to: start.clone().add(1, 'month') };
    }
  }
};

/** Report rows added up per currency (SEK and NOK are never added together). */
export const totalsByCurrency = (rows) =>
  Object.values(
    rows.reduce((totals, row) => {
      const currency = `${row.currency || ''}`.toUpperCase();
      const total = totals[currency] || { currency };
      [
        'payments',
        'gross',
        'applicationFee',
        'payout',
        'stripeFee',
        'platformNet',
        'missingDetails',
      ].forEach((field) => {
        total[field] = (total[field] || 0) + (Number(row[field]) || 0);
      });
      return { ...totals, [currency]: total };
    }, {})
  );
